import * as angular from 'angular';
import { EncField, EncodingResult, SortType } from '@mode-vegas/vegas';

import { FlamingoTypes } from '@mode/shared/contract-common';

import { FieldComponent, FieldController } from './field.component';
import { ColumnRow } from './viz-designer-icons';
import { EncodingHelpers } from '@mode/charts/util-vegas';

class TableFieldController extends FieldController {
  static $inject = ['$element'];

  model: FlamingoTypes.ModeModel;
  view: FlamingoTypes.ModeView;

  onUpdate: (args: { $result: EncodingResult }) => void;
  onRemove: (args: { $field: EncField }) => void;
  onRemoveSort: (args: { $source: string }) => void;

  constructor(private readonly $element: angular.IAugmentedJQuery) {
    super();
    this.isSortAllowed = true;
  }

  $onChanges(changes: angular.IOnChangesObject) {
    super.$onChanges(changes);

    if (changes.view) {
      if (this.view != null) {
        this.isValid = this.formula.isValid
          ? EncodingHelpers.validateDuplicateCalcFields(this.model, this.view.name, this.formula.attrName)
          : this.formula.isValid;
        this.isValid = this.isValid && !this.formula.isNonScalarCalc;
      }
    }

    // Validate sort encoding, if any
    if (changes.field && this.isValid && this.schemaModel != null) {
      const sortField = this.encoding.sorts.find((s) => s.source === this.field.formula);
      if (sortField && sortField.type === SortType.FIELD) {
        const errors: string[] = [];
        EncodingHelpers.validateEncSortField(sortField, this.schemaModel, errors);

        this.isValid = !errors.length;
        if (!this.isValid) {
          this.error = errors[0];
        }
      }
    }
  }

  init() {
    super.init();
    if (this.formula.isNonScalarCalc) {
      this.isValid = false;
      const exprStage = this.formula.isAnalyticCalc ? 'Analytic' : 'Aggregate';
      this.error = `${exprStage} values are not supported in table columns`;
    }
  }

  $postLink() {
    this.$element[0].addEventListener('dragstart', (evt: any) => {
      if (evt.dataTransfer) {
        evt.dataTransfer.setData('text/plain', evt.target.id);
      }
    });
  }

  buildContextMenuOptions() {
    const opts = [];

    if (this.hasSort) {
      opts.push(
        {
          html: this.getMenuItemHtml(false, 'Clear Sort'),
          click: () => this.removeSort(),
        },
        null
      );
    }

    opts.push({
      html: this.getMenuItemHtml(false, 'Remove'),
      click: () => this.removeField(),
    });

    return opts;
  }

  removeSort(): void {
    this.onRemoveSort({ $source: this.formula.formula });
  }

  removeField(): void {
    this.onRemove({ $field: this.field });
  }

  dispatchEncodingUpdate(result: EncodingResult) {
    this.onUpdate({ $result: result });
  }

  get canToggleDimension(): boolean {
    return !(this.isNames || this.isValues);
  }

  get sortDirectionIcon(): string {
    if (this.encoding && this.encoding.sorts) {
      const sort = this.encoding.sorts.filter((s) => s.source === this.field.formula)[0];
      if (sort && this.formula) {
        if (this.formula.isNames) {
          return sort.descending ? ColumnRow.sortAscNames : ColumnRow.sortDescNames;
        }

        if (this.formula.isDiscrete) {
          return sort.descending ? ColumnRow.sortDescDiscrete : ColumnRow.sortAscDiscrete;
        }
      }
    }

    return null;
  }
}

export const TableFieldComponent: angular.IComponentOptions = {
  ...FieldComponent,
  bindings: {
    ...FieldComponent.bindings,
    varType: '@',
    onUpdate: '&',
    onRemove: '&',
    onRemoveSort: '&',
    view: '<',
  },
  controller: TableFieldController,
};
